// =============================================================================
// LIGA AJUPAM — Velo sobre Tabla y Partidos (v6.11.48)
// =============================================================================
// Antes, a un equipo bloqueado se le tapaba el INICIO entero con una pantalla
// roja — justo la pantalla desde la que podía pagar o invitar a su compañero.
// Ahora es al revés: Inicio y Mi Equipo quedan abiertos para que se regularice,
// y lo que se cierra son las secciones de competencia, que es lo que realmente
// no le corresponde ver todavía.
//
// El contenido real queda debajo, borroneado y sin interacción: se lee que hay
// algo ahí (no es una pantalla de error) pero no se puede usar ni leer datos.

import React from 'react';
import { Lock, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
import { getLockCopy } from '../utils/blockCopy';
import type { SectionLockReason } from '../utils/sectionAccess';

interface LockedSectionOverlayProps {
  reason: SectionLockReason | null;
  regularPhaseStartDate?: string | null;
  /** Lleva al Inicio, que es donde se resuelve. */
  onGoHome?: () => void;
  children: React.ReactNode;
}

export const LockedSectionOverlay: React.FC<LockedSectionOverlayProps> = ({
  reason,
  regularPhaseStartDate,
  onGoHome,
  children,
}) => {
  const copy = getLockCopy(reason, { regularPhaseStartDate });

  return (
    <div className="relative">
      {/* aria-hidden + inert: el contenido borroneado no debe ser navegable ni
          leído por lectores de pantalla. */}
      <div
        aria-hidden="true"
        // @ts-expect-error -- `inert` todavía no está en los tipos de React
        inert=""
        className="pointer-events-none select-none blur-[6px] opacity-50 saturate-50"
      >
        {children}
      </div>

      <div className="absolute inset-0 flex items-start justify-center px-4 pt-16 pb-8 overflow-y-auto">
        <div className="w-full max-w-sm bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl p-6 text-center shadow-xl">
          <div className="flex justify-center mb-4">
            <div className="bg-gray-100 rounded-full p-4">
              <Lock size={30} className="text-gray-500" />
            </div>
          </div>
          <h2 className="text-lg font-bold text-gray-800 font-display">{copy.heading}</h2>
          <p className="text-sm text-gray-600 mt-2 leading-snug">{copy.lead}</p>
          {copy.action && (
            <p className="text-sm text-gray-700 mt-3 leading-snug font-medium">{copy.action}</p>
          )}
          {onGoHome && (
            <Button
              variant="primary"
              fullWidth
              className="mt-5"
              onClick={onGoHome}
            >
              Ir a Inicio
              <ArrowRight size={16} className="ml-1.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
